import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../complaints/ComplaintManagement.css';
import RTPageLayout from '../common/RTPageLayout';

const AddStaffType = ({ type, onClose }) => {
  const location = useLocation();
  const isEditMode = type && type.type_id;

  const [form, setForm] = useState({
    type_name: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      setForm({
        type_name: type.type_name || '',
        description: type.description || '',
      });
    } else {
      setForm({
        type_name: '',
        description: '',
      });
    }
  }, [type, isEditMode]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.type_name.trim()) {
      newErrors.type_name = 'Staff type name is required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type_name: form.type_name.trim(),
        description: form.description.trim() || null,
      };

      if (isEditMode) {
        await apiClient.post(`/staff/types/${type.type_id}/update`, payload);
        toastSuccess('Staff type updated successfully!');
      } else {
        await apiClient.post('/staff/types', payload);
        toastSuccess('Staff type added successfully!');
      }

      onClose();
    } catch (error) {
      console.error('Error saving staff type:', error);

      // Show comprehensive error messages
      let errorMessage = 'Failed to save staff type';

      // Check for duplicate errors more comprehensively
      const errorDetail = error.response?.data?.detail || '';
      const errorMessage_response = error.response?.data?.message || '';
      const allErrorText = (
        errorDetail +
        ' ' +
        errorMessage_response
      ).toLowerCase();

      if (
        error.response?.status === 400 &&
        allErrorText.includes('already exists')
      ) {
        errorMessage = 'A staff type with this name already exists';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided';
      } else if (error.response?.status === 404) {
        errorMessage = 'Staff type not found';
      }

      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <RTPageLayout
      activePath={location.pathname}
      title={<>{isEditMode ? 'Edit Staff Type' : 'Add Staff Type'}</>}
      actions={
        <>
          <RTButton variant="secondary" onClick={onClose}>
            ← Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <form onSubmit={handleSubmit} className="complaint-form">
        <div className="complaint-form-sections">
          <div className="complaint-section">
            <h3 className="complaint-section-title">Type Details</h3>

            <div className="complaint-form-group">
              <label>
                Staff Type Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="type_name"
                value={form.type_name}
                onChange={handleChange}
                placeholder="Enter staff type name (e.g., Maintenance, Security, Cleaning)"
                required
                disabled={loading}
              />
              {errors.type_name && (
                <span className="form-error">{errors.type_name}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Enter description for this staff type (optional)"
                rows="4"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <RTSpinner
              size="medium"
              showMessage={true}
              message="Saving staff type..."
            />
          </div>
        )}

        <div className="complaint-actions">
          <RTButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </RTButton>
          <RTButton type="submit" variant="primary" disabled={loading}>
            {loading
              ? 'Saving...'
              : isEditMode
                ? 'Update Staff Type'
                : 'Add Staff Type'}
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddStaffType;
